import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronDown, ChevronRight, Settings } from 'lucide-react';
import { NAV } from './utils/constants';
import { cx } from './components/shared/ui.jsx';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from './assets/logo/probestackLogo.png';
import AppRoutes from './AppRoutes.jsx';
import { initAuth } from './http-service/client.js';
import { Header as LibraryHeader, ProfileDropdown } from '@probestack/probestack-ui-library';
import { useProbeStackProfile, loadProfileOnce, SessionError } from './hooks/useProbeStackProfile';
import { useTheme } from './contexts/ThemeContext.jsx';
import { HeaderErrorBoundary } from './ui/HeaderErrorBoundary.jsx';
import { clearAuthCookies } from './utils/auth.js';
import { getDisplayName } from './lib/userUtils';
import { markSessionVerified, redirectToLogin, SKIP_AUTH } from './utils/auth.js';

const AI_GATEWAY_BASE_PATH = "/ai-gateway";

export default function App() {
  const [authChecked, setAuthChecked] = useState(SKIP_AUTH);
  const [authError, setAuthError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const activePageId = location.pathname
    .replace(`${AI_GATEWAY_BASE_PATH}/`, '')
    .split('/')[0] || 'dashboard';

  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem('fs_sidebar_pinned') === 'true'; } catch { return false; }
  });
  const [hovering, setHovering] = useState(false);
  const [suppressHover, setSuppressHover] = useState(false);
  const [openSections, setOpenSections] = useState(() =>
    Object.fromEntries(NAV.filter(entry => entry.items).map(entry => [entry.id, true]))
  );
  const toggleSection = (id) => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));

  const expanded = pinned || (hovering && !suppressHover);

  useEffect(() => {
    try { localStorage.setItem('fs_sidebar_pinned', pinned); } catch {}
  }, [pinned]);

  const handleMouseEnter = () => setHovering(true);
  const handleMouseLeave = () => {
    setHovering(false);
    setSuppressHover(false);
  };

  const handlePinClick = () => setPinned(true);
  const handleUnpinClick = () => {
    setPinned(false);
    setSuppressHover(true); // collapses immediately even if still hovering
  };

  const toggleSidebarPin = () => (pinned ? handleUnpinClick() : handlePinClick());

  // The SSO session lives in an HttpOnly cookie JS can't read, so ask the
  // admin-backend (/me, proxied same-origin by nginx) instead. Only an
  // explicit 401/403 sends the user to login; any other failure shows an
  // error rather than redirecting, so a backend outage can't cause a
  // login <-> app redirect loop.
  useEffect(() => {
    if (SKIP_AUTH) return;
    let cancelled = false;
    loadProfileOnce()
      .then(() => {
        if (cancelled) return;
        markSessionVerified();
        setAuthChecked(true);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof SessionError && (err.status === 401 || err.status === 403)) {
          redirectToLogin();
          return;
        }
        setAuthError(true);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    initAuth();
  }, []);

  // Identity comes from the session token via the admin-backend (same call
  // the Profile page makes). localStorage is only a fallback while that
  // request is in flight or if it fails — it can hold a previous session's
  // user, which is what made the header show the wrong name.
  const profile = useProbeStackProfile();
  const { userType } = profile;
  const loggedInUserEmail = profile.email || localStorage.getItem("userEmail") || "";
  const loggedInUserRole = profile.role || localStorage.getItem("userRole") || "User";
  const loggedInUserName = profile.name || getDisplayName();

  // Real Starter/Enterprise badge — same admin-backend `user_type` field
  // and mapping the Profile page uses, instead of a hardcoded default.
  const accountType = userType === 'organization' ? 'enterprise' : 'starter';
  const { theme, setTheme } = useTheme();

  const handleLogout = () => {
    localStorage.clear();
    // Best-effort clear of the shared ps_auth_* SSO cookies too — without
    // this, the platform's other apps (console./community.probestack.io,
    // etc.) still see a valid session cookie after "signing out" here and
    // the user appears to stay logged in everywhere else.
    clearAuthCookies();
    window.location.href = 'https://probestack.io/logout';
  };

  if (authError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-dark-800 font-body text-foreground">
        <p>We couldn't verify your session. Please try again.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-primary/10"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-800 font-body text-foreground">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-dark-800 font-body text-foreground">
      {/* Full-width header — still the LibraryHeader shell (rightSlot /
          ProfileDropdown come from @probestack/probestack-ui-library), but
          the brand block is passed as a fully custom `logo` node so the
          original height/logo size/font size/background match exactly —
          those aren't exposed as props on the library's built-in
          logoImageSrc+productName layout. Config entries and the theme
          toggle live inside the ProfileDropdown rather than as separate
          header controls. */}
      <div className="probestack-ui-library sticky top-0 z-50 shrink-0">
        <HeaderErrorBoundary
          fallback={
            <div className="!h-16 !flex !items-center !bg-[var(--c-surface-080826)] !border-b !border-[var(--c-border-1f2840)] px-5">
              <img src={logo} alt="" className="h-10 w-auto shrink-0" />
              <span className="gradient-text font-heading text-lg sm:text-xl font-extrabold leading-tight ml-2.5">
                ForgeSphere AI Gateway
              </span>
              <span className="ml-auto text-xs text-muted-foreground">
                Session issue loading account info — try refreshing or signing in again.
              </span>
            </div>
          }
        >
        <LibraryHeader
          logo={
            <button
              type="button"
              className="flex shrink-0 items-center gap-2.5 text-left cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => {
                window.location.href = 'https://forgesphere.probestack.io';
              }}
            >
              <img src={logo} alt="" className="h-10 w-auto shrink-0" />
              <div className="flex flex-col justify-center overflow-hidden whitespace-nowrap">
                <span className="text-[0.65rem] leading-tight text-[var(--c-foreground-9ca3af)]">Probestack</span>
                <span className="gradient-text font-heading text-lg sm:text-xl font-extrabold leading-tight">
                  ForgeSphere AI Gateway
                </span>
              </div>
            </button>
          }
          className="!h-16 !grid !items-center !content-center !py-0 !bg-[var(--c-surface-080826)] !border-b !border-[var(--c-border-1f2840)] px-5"
          rightSlot={
            <div className="probestack-ui-library">
              <ProfileDropdown
                user={{
                  name: loggedInUserName,
                  email: loggedInUserEmail,
                  role: loggedInUserRole,
                  accountType,
                }}
                items={[
                  {
                    label: "Configuration",
                    icon: <Settings size={16} />,
                    children: [
                      {
                        label: "Git Configuration",
                        onClick: () => navigate(`${AI_GATEWAY_BASE_PATH}/configs/git`),
                        icon: <Settings size={16} />,
                      },
                      {
                        label: "Google Cloud Storage",
                        onClick: () => navigate(`${AI_GATEWAY_BASE_PATH}/configs/storage`),
                        icon: <Settings size={16} />,
                      },
                      {
                        label: "API Standard Linting",
                        onClick: () => navigate(`${AI_GATEWAY_BASE_PATH}/configs/api-standard-linting`),
                        icon: <Settings size={16} />,
                      },
                      {
                        label: "API Custom Linting",
                        onClick: () => navigate(`${AI_GATEWAY_BASE_PATH}/configs/api-custom-linting`),
                        icon: <Settings size={16} />,
                      },
                    ],
                  },
                ]}
                onSignOut={handleLogout}
                onProfileClick={() => navigate(`${AI_GATEWAY_BASE_PATH}/profile`)}
                showThemeToggle={true}
                theme={theme}
                onThemeChange={setTheme}
              />
            </div>
          }
        />
        </HeaderErrorBoundary>
      </div>

      <div className="flex min-h-0 flex-1">
        <aside
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={cx(
            'sticky top-16 flex h-[calc(100vh-4rem)] shrink-0 flex-col border-r border-[var(--border)] transition-[width] duration-300 ease-in-out',
            expanded ? 'w-56' : 'w-16',
          )}
          style={{
            background: 'linear-gradient(180deg, color-mix(in srgb, var(--background-card) 97%, transparent) 0%, color-mix(in srgb, var(--background) 99%, transparent) 100%)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
          }}
        >
          <div className="flex h-full flex-col overflow-y-auto overflow-x-hidden">
            <nav className={cx('flex-1 py-2', expanded ? 'px-[7px]' : 'px-1')}>
              {NAV.map(entry => {
                if (entry.items) {
                  const isOpen = openSections[entry.id];
                  return (
                    <div key={entry.id} className="mb-1">
                      {expanded && (
                        <button
                          type="button"
                          onClick={() => toggleSection(entry.id)}
                          className="mb-0.5 flex w-full cursor-pointer items-center gap-1.5 rounded-md px-3 py-1.5 text-left text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--c-foreground-9ca3af)] transition-colors hover:text-[var(--c-foreground-e5e7eb)]"
                        >
                          {isOpen
                            ? <ChevronDown className="h-3 w-3 shrink-0" />
                            : <ChevronRight className="h-3 w-3 shrink-0" />}
                          <span className="overflow-hidden whitespace-nowrap">{entry.label}</span>
                        </button>
                      )}
                      {(isOpen || !expanded) && entry.items.map(item => {
                        const active = activePageId === item.id;
                        const Icon = item.icon;

                        return (
                          <Link
                            key={item.id}
                            to={`${AI_GATEWAY_BASE_PATH}/${item.id}`}
                            title={expanded ? undefined : item.label}
                            className={cx(
                              'mb-px flex w-full items-center gap-3 rounded-xl border-l-2 py-2.5 text-left text-sm font-medium no-underline transition-all duration-150',
                              expanded ? 'px-3' : 'justify-center px-1',
                              active
                                ? 'border-l-[#ff8a5c]/80 bg-gradient-to-r from-[color-mix(in_srgb,var(--c-accent-ff5b1f)_20%,transparent)] via-[color-mix(in_srgb,var(--c-accent-ff5b1f)_10%,transparent)] to-[color-mix(in_srgb,var(--c-accent-ff5b1f)_5%,transparent)] text-[var(--c-accent-ff8a5c)]'
                                : 'border-l-transparent text-muted-foreground hover:bg-[var(--overlay-hover)] hover:text-foreground'
                            )}
                          >
                            <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                            {expanded && <span className="overflow-hidden whitespace-nowrap">{item.label}</span>}
                          </Link>
                        );
                      })}
                    </div>
                  );
                }

                const active = activePageId === entry.id;
                const Icon = entry.icon;

                return (
                  <Link
                    key={entry.id}
                    to={`${AI_GATEWAY_BASE_PATH}/${entry.id}`}
                    title={expanded ? undefined : entry.label}
                    className={cx(
                      'mb-px flex w-full items-center gap-3 rounded-xl border-l-2 py-2.5 text-left text-sm font-medium no-underline transition-all duration-150',
                      expanded ? 'px-3' : 'justify-center px-1',
                      active
                        ? 'border-l-[#ff8a5c]/80 bg-gradient-to-r from-[color-mix(in_srgb,var(--c-accent-ff5b1f)_20%,transparent)] via-[color-mix(in_srgb,var(--c-accent-ff5b1f)_10%,transparent)] to-[color-mix(in_srgb,var(--c-accent-ff5b1f)_5%,transparent)] text-[var(--c-accent-ff8a5c)]'
                        : 'border-l-transparent text-muted-foreground hover:bg-[var(--overlay-hover)] hover:text-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                    {expanded && <span className="overflow-hidden whitespace-nowrap">{entry.label}</span>}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* pin toggle */}
          <button
            type="button"
            onClick={toggleSidebarPin}
            title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
            className={cx(
              'group absolute -right-3.5 top-8 z-50',
              'flex h-7 w-7 items-center justify-center rounded-full',
              'border border-[color-mix(in_srgb,var(--c-border-ffffff)_10%,transparent)] bg-[var(--c-surface-0b0e21)]',
              'shadow-[0_2px_14px_rgba(0,0,0,0.55)]',
              'transition-all duration-200',
              'hover:border-[color-mix(in_srgb,var(--c-accent-ff5b1f)_50%,transparent)] hover:bg-[color-mix(in_srgb,var(--c-accent-ff5b1f)_10%,transparent)] hover:shadow-[0_0_14px_rgba(255,91,31,0.28)]',
            )}
          >
            <ChevronLeft
              style={{ width: 14, height: 14 }}
              className={cx(
                'text-[var(--c-foreground-6b7280)] transition-all duration-300 group-hover:text-[var(--c-accent-ff8a5c)]',
                !pinned && 'rotate-180',
              )}
            />
          </button>
        </aside>

        <main className="flex min-h-0 flex-1 overflow-y-auto px-[22px] py-5 bg-[var(--c-surface-0e172a)]">
          {/* `main` has a fixed height (the shell is h-screen), so a page
              stretched directly inside it was capped at that height and its
              overflow lost the bottom padding — the last card sat flush
              against the window edge. self-start lets the page grow with its
              content; min-h-full keeps short pages filling the area. */}
          <div className="flex min-h-full w-full min-w-0 self-start">
            <AppRoutes />
          </div>
        </main>
      </div>
    </div>
  );
}
